import { createFileRoute, Link } from "@tanstack/react-router";
import { Database, Workflow, ShoppingCart, FileText, Users, BarChart3, ArrowRight } from "lucide-react";
import { ServicePage } from "@/components/sections/service-page";
import { PageShell } from "@/components/site/page-shell";
import { TopRelatedDigitalItems } from "@/components/sections/top-related-digital-items";

export const Route = createFileRoute("/services/erp-solution/")({
  component: ErpIndex,
  head: () => ({
    meta: [
      { title: "ERP Solution — CoderLity" },
      { name: "description", content: "Integrated ERP solutions for finance, inventory, HR, sales and operations." },
      { property: "og:title", content: "ERP Solution — CoderLity" },
      { property: "og:description", content: "Unify finance, inventory, HR and operations on one connected ERP platform." },
      { property: "og:url", content: "/services/erp-solution" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/services/erp-solution" }],
  }),
});

const modules = [
  { slug: "inventory-warehouse", title: "Inventory & Warehouse", desc: "Explore inventory & warehouse" },
  { slug: "sales-purchase", title: "Sales & Purchase", desc: "Explore sales & purchase" },
  { slug: "manufacturing", title: "Manufacturing", desc: "Explore manufacturing" },
  { slug: "supply-chain", title: "Supply Chain", desc: "Explore supply chain" },
];

const finance = [
  { slug: "accounting-invoicing", title: "Accounting & Invoicing", desc: "Explore accounting & invoicing" },
  { slug: "reporting-analytics", title: "Reporting & Analytics", desc: "Explore reporting & analytics" },
  { slug: "payroll", title: "Payroll", desc: "Explore payroll" },
];

const customization = [
  { slug: "workflow-automation", title: "Workflow Automation", desc: "Explore workflow automation" },
  { slug: "role-based-permissions", title: "Role-based Permissions", desc: "Explore role-based permissions" },
  { slug: "data-migration-setup", title: "Data Migration & Setup", desc: "Explore data migration & setup" },
  { slug: "training-support", title: "Training & Support", desc: "Explore training & support" },
];

function Group({ title, items }: { title: string; items: { slug: string; title: string; desc: string }[] }) {
  return (
    <section className="py-12">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <h2 className="text-3xl font-bold tracking-tight">{title}</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((it) => (
            <Link
              key={it.slug}
              to="/services/erp-solution/$child"
              params={{ child: it.slug }}
              className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition"
            >
              <h3 className="text-lg font-semibold">{it.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground inline-flex items-center gap-1">
                {it.desc} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function ErpIndex() {
  return (
    <>
      <ServicePage data={data} />
      <PageShell>
        <Group title="Modules" items={modules} />
        <Group title="Finance" items={finance} />
        <Group title="Customization" items={customization} />
      </PageShell>
      <TopRelatedDigitalItems hub="erp" />
    </>
  );
}

const data = {
  eyebrow: "Services",
  title: "ERP Solution",
  sub: "Integrated solutions for managing your business.",
  intro:
    "Stop juggling spreadsheets, accounting software, inventory tools and HR systems that don't talk to each other. Our ERP solutions unify finance, inventory, HR, sales and operations on one platform — with real-time dashboards, automated workflows and the integrations your business actually uses. Built for SMBs and mid-market companies who outgrew QuickBooks but don't need SAP.",
  features: [
    { icon: Database, title: "Single source of truth", description: "Customers, products, orders and finances in one connected system." },
    { icon: Workflow, title: "Workflow automation", description: "Approval flows, recurring billing and auto-generated journal entries." },
    { icon: ShoppingCart, title: "Inventory + POS", description: "Multi-warehouse stock, barcode scanning and POS integration." },
    { icon: FileText, title: "Accounting + invoicing", description: "Double-entry ledger, tax handling and one-click invoice generation." },
    { icon: Users, title: "HR + payroll", description: "Employee records, time-off, expense claims and payroll exports." },
    { icon: BarChart3, title: "Real-time dashboards", description: "Cash flow, sales pipeline and inventory health at a glance." },
  ],
  process: [
    { step: "01", title: "Discovery", description: "Map current processes, pain points and integration needs." },
    { step: "02", title: "Configure", description: "Modules, workflows, chart of accounts and user roles." },
    { step: "03", title: "Migrate", description: "Import historical data from QuickBooks, Xero, Excel or legacy ERP." },
    { step: "04", title: "Train & launch", description: "Team training, parallel run and go-live support." },
  ],
  deliverables: [
    "Fully configured ERP platform",
    "Migrated historical data with reconciliation",
    "Custom workflows and approval chains",
    "Integrations with banking, e-commerce and shipping",
    "Role-based dashboards and reports",
    "Team training and process documentation",
  ],
  techStack: ["ERPNext", "Odoo", "Custom React + Node", "PostgreSQL", "Supabase", "Stripe", "QuickBooks API", "Shopify API"],
  pricing: [
    { name: "Starter", price: "$8k", features: ["Finance + inventory", "Up to 10 users", "Standard integrations", "4 weeks"] },
    { name: "Business", price: "$25k", period: "project", highlighted: true, features: ["All core modules", "Custom workflows", "Data migration", "8–12 weeks"] },
    { name: "Managed ERP", price: "$3k", period: "month", features: ["Hosting + backups", "Ongoing customization", "User support", "Quarterly upgrades"] },
  ],
  faqs: [
    { q: "Custom ERP or off-the-shelf?", a: "We start with proven platforms (ERPNext, Odoo) and customize only where you have real differentiation. Pure custom builds are reserved for unique operational models." },
    { q: "Can it replace QuickBooks?", a: "Yes — and add inventory, CRM, HR and project management on top. We migrate your chart of accounts, customers and historical transactions during onboarding." },
    { q: "Will it integrate with my Shopify / Stripe / bank?", a: "Yes. We have pre-built connectors for major e-commerce, payments and banking platforms, plus custom integrations for anything else." },
    { q: "What about multi-currency and multi-company?", a: "Both supported out of the box, with consolidated reporting and inter-company transactions." },
  ],
};
